"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import { useParams } from "next/navigation"
import type { CohortResults } from "@/schema"
import { useVirtualizer } from "@tanstack/react-virtual"
import { Button } from "@ui/shad/button"
import { Checkbox } from "@ui/shad/checkbox"
import { Bookmark, Download, Loader2, Users } from "lucide-react"
import { toast } from "sonner"

import { createCohortCsvExport, fetchCohortCsvExport, useCohortResultPages } from "@/hooks/api/cohorts"
import { useApiContext } from "@/hooks/api/use-api-context"
import { FullscreenDialog } from "@/components/dialogs"
import PeekView from "@/components/peek-view"

import { CohortBulkActions } from "./cohort-bulk-actions"
import { SaveAsReportDialog } from "./save-as-report-dialog"

interface CohortDialogProps {
  cohortId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  showSaveAsReport?: boolean
}

// Cohort record types that map to a peekable/linkable record. `task` has no peek view.
const PEEK_RECORD_TYPES: Record<
  CohortResults["record_type"],
  "Client" | "Prospect" | "Person" | "Contact" | "Meeting" | null
> = {
  client: "Client",
  prospect: "Prospect",
  person: "Person",
  contact: "Contact",
  meeting: "Meeting",
  task: null,
}

const COLUMN_WIDTH = 180
const FIRST_COLUMN_WIDTH = 220
const BULK_ACTION_LIMIT = 500

export default function CohortDialog(props: CohortDialogProps) {
  return <CohortDialogContent key={`${props.cohortId}-${props.open}`} {...props} />
}

function CohortDialogContent({ cohortId, open, onOpenChange, showSaveAsReport = true }: CohortDialogProps) {
  const { data, isLoading, error, retry, isFetching, rowCount, getRow, setVisibleRange } = useCohortResultPages(
    open ? cohortId : null
  )
  const scrollRef = useRef<HTMLDivElement>(null)
  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 38,
    overscan: 10,
    scrollMargin: 37,
  })
  const virtualRows = virtualizer.getVirtualItems()
  const firstVisibleRow = virtualRows[0]?.index ?? 0
  const lastVisibleRow = virtualRows.at(-1)?.index ?? 0

  useEffect(() => {
    if (!error) setVisibleRange({ start: firstVisibleRow, end: lastVisibleRow })
  }, [error, firstVisibleRow, lastVisibleRow, setVisibleRange])
  const getContext = useApiContext()
  const { organizationId } = useParams<{ organizationId?: string }>()
  const [isDownloading, setIsDownloading] = useState(false)
  const [saveReportOpen, setSaveReportOpen] = useState(false)
  const [selectedRecordIds, setSelectedRecordIds] = useState<string[]>([])
  const downloadGeneration = useRef(0)

  useEffect(() => {
    setIsDownloading(false)
    return () => {
      downloadGeneration.current += 1
    }
  }, [cohortId, open])

  const hasBulkActions = !organizationId && (data?.record_type === "client" || data?.record_type === "prospect")
  const selectedRecords = useMemo(() => new Set(selectedRecordIds), [selectedRecordIds])
  const allSelected = !!data?.record_ids.length && data.record_ids.every((id) => selectedRecords.has(id))

  function toggleRecordSelection(recordId: string, checked: boolean) {
    setSelectedRecordIds((current) =>
      checked ? [...new Set([...current, recordId])] : current.filter((id) => id !== recordId)
    )
  }

  async function handleDownloadCsv() {
    setIsDownloading(true)
    const generation = downloadGeneration.current
    try {
      const context = await getContext()
      if (!context) return
      if (!data) return
      let result = await createCohortCsvExport(context, cohortId, data.result_id)
      for (let attempt = 0; result.status === "pending" && attempt < 120; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, 1000))
        if (generation !== downloadGeneration.current) return
        result = await fetchCohortCsvExport(context, cohortId, data.result_id)
      }
      if (generation !== downloadGeneration.current) return
      if (result.status !== "ready" || !result.download_url) {
        throw new Error(result.error || "Export is still being prepared. Click Download CSV to check again.")
      }
      const link = document.createElement("a")
      link.href = result.download_url
      link.download = ""
      link.target = "_blank"
      link.rel = "noopener noreferrer"
      link.click()
    } catch (error) {
      if (generation !== downloadGeneration.current) return
      toast.error(error instanceof Error ? error.message : "Failed to download CSV")
    } finally {
      if (generation === downloadGeneration.current) setIsDownloading(false)
    }
  }

  const peekRecordType = data && !organizationId ? PEEK_RECORD_TYPES[data.record_type] : null

  const columns = useMemo(
    () =>
      data?.columns.map((name, index) => ({
        name,
        label: formatColumnHeader(name),
        width: index === 0 ? FIRST_COLUMN_WIDTH : COLUMN_WIDTH,
      })) ?? [],
    [data?.columns]
  )
  const totalWidth = columns.reduce((sum, column) => sum + column.width, 0)

  return (
    <>
      <FullscreenDialog open={open} onOpenChange={onOpenChange} title={data?.description || "Cohort"}>
        <div className="flex h-full flex-col">
          <div className="mb-3 flex items-center gap-3">
            <div className="bg-muted flex size-8 items-center justify-center rounded-md">
              <Users className="text-muted-foreground size-4" />
            </div>
            <div className="flex-1">
              {data && (
                <p className="text-muted-foreground flex items-center gap-2 text-xs">
                  {data.record_count} {data.record_type}s
                  {isFetching && (
                    <span role="status" aria-label="Loading rows">
                      <Loader2 className="size-3 animate-spin" />
                    </span>
                  )}
                </p>
              )}
            </div>
            {data?.row_count ? (
              <div className="flex items-center gap-2">
                {showSaveAsReport && (
                  <Button variant="outline" size="sm" onClick={() => setSaveReportOpen(true)}>
                    <Bookmark />
                    Save as report
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={handleDownloadCsv} disabled={isDownloading}>
                  {isDownloading ? <Loader2 className="animate-spin" /> : <Download />}
                  {isDownloading ? "Preparing download…" : "Download CSV"}
                </Button>
              </div>
            ) : null}
          </div>

          {/* Table area */}
          <div
            ref={scrollRef}
            className="relative flex-1 overflow-auto"
            data-cohort-scroll
            tabIndex={0}
            role="region"
            aria-label="Cohort results"
          >
            {isLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="text-muted-foreground size-5 animate-spin" />
              </div>
            ) : error ? (
              <div className="flex flex-col items-center gap-2 py-16 text-sm">
                <p>{error.message}</p>
                <Button variant="outline" size="sm" onClick={retry}>
                  Retry
                </Button>
              </div>
            ) : !data?.row_count ? (
              <div className="text-muted-foreground flex items-center justify-center py-16 text-sm">No results</div>
            ) : (
              <div role="table" aria-label="Cohort" aria-rowcount={data.row_count + 1} style={{ minWidth: totalWidth }}>
                <div role="row" aria-rowindex={1} className="bg-background sticky top-0 z-10 flex w-full">
                  {columns.map((column, index) => (
                    <div
                      key={column.name}
                      role="columnheader"
                      className="text-muted-foreground flex h-[37px] flex-1 items-center gap-2 border-r border-b px-2 text-xs font-medium"
                      style={{ flexBasis: column.width, minWidth: column.width }}
                    >
                      {index === 0 && hasBulkActions && (
                        <Checkbox
                          checked={allSelected ? true : selectedRecordIds.length > 0 ? "indeterminate" : false}
                          onCheckedChange={() => setSelectedRecordIds(allSelected ? [] : (data.record_ids ?? []))}
                          disabled={!data.record_ids.length}
                          aria-label={allSelected ? "Deselect all rows" : "Select all rows"}
                          title={
                            data.record_count > BULK_ACTION_LIMIT ? "Select up to 500 records individually" : undefined
                          }
                          className="border-border shrink-0 shadow-none"
                        />
                      )}
                      {column.label}
                    </div>
                  ))}
                </div>
                <div className="relative" style={{ height: virtualizer.getTotalSize() }}>
                  {virtualRows.map((virtualRow) => {
                    const row = getRow(virtualRow.index)
                    return (
                      <div
                        key={virtualRow.key}
                        role="row"
                        aria-selected={
                          hasBulkActions && !!row?.recordId ? selectedRecords.has(row.recordId) : undefined
                        }
                        aria-rowindex={virtualRow.index + 2}
                        data-state={row?.recordId && selectedRecords.has(row.recordId) ? "selected" : undefined}
                        className="hover:bg-tinted data-[state=selected]:bg-muted absolute top-0 left-0 flex h-[38px] w-full"
                        style={{ transform: `translateY(${virtualRow.start - 37}px)` }}
                      >
                        {columns.map((column, index) => {
                          const value = row?.values[index]
                          return (
                            <div
                              key={column.name}
                              role="cell"
                              className="flex min-w-0 flex-1 items-center gap-2 truncate border-r border-b px-2 text-sm"
                              style={{ flexBasis: column.width, minWidth: column.width }}
                            >
                              {index === 0 && hasBulkActions && (
                                <Checkbox
                                  checked={!!row?.recordId && selectedRecords.has(row.recordId)}
                                  disabled={
                                    !row?.recordId ||
                                    (selectedRecordIds.length >= BULK_ACTION_LIMIT &&
                                      !selectedRecords.has(row.recordId))
                                  }
                                  onCheckedChange={(checked) => {
                                    if (row?.recordId) toggleRecordSelection(row.recordId, checked === true)
                                  }}
                                  aria-label="Select row"
                                  className="border-border shrink-0 shadow-none"
                                />
                              )}
                              {!row ? (
                                <span className="bg-muted h-3 w-20 animate-pulse rounded" />
                              ) : value === null || value === undefined ? (
                                <span className="text-muted-foreground">-</span>
                              ) : index === 0 && peekRecordType && row.recordId ? (
                                <PeekView
                                  recordType={peekRecordType}
                                  recordId={row.recordId}
                                  label={String(value)}
                                  size="sm"
                                />
                              ) : (
                                <span className="truncate">{String(value)}</span>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Bulk actions */}
          {hasBulkActions && data && selectedRecordIds.length > 0 ? (
            <div className="flex items-center justify-center py-3">
              <CohortBulkActions
                recordIds={selectedRecordIds}
                recordType={data.record_type as "client" | "prospect"}
                recordCount={selectedRecordIds.length}
                onClearSelection={() => setSelectedRecordIds([])}
              />
            </div>
          ) : null}
          {hasBulkActions && data && data.record_count > BULK_ACTION_LIMIT && selectedRecordIds.length > 0 && (
            <p className="text-muted-foreground py-2 text-center text-xs">
              Select up to 500 records for bulk actions. Download CSV includes the full report.
            </p>
          )}
        </div>
      </FullscreenDialog>
      <SaveAsReportDialog
        open={saveReportOpen}
        setOpen={setSaveReportOpen}
        cohortId={cohortId}
        defaultName={data?.description ?? ""}
      />
    </>
  )
}

function formatColumnHeader(col: string): string {
  return col.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
}
